import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Check, Download, Eye, HelpCircle, KeyRound, Lock, Pencil, Plus, Power, User, X } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { useSelector } from 'react-redux';
import { useToast } from '../../Common/context/ToastContext';
import { useTooltip } from '../../Common/context/TooltipContext';
import ReceiptPreviewModalV2 from '../../Common/components/receipt/ReceiptPreviewModalV2';
import { RECEIPT_UI_VERSION } from '../../Common/config/receiptUi';
import { RECEIPT_CARD_SMS_NOTICE, RECEIPT_LABELS } from '../../Common/constants/receiptLabels';
import { BRAND_COPYRIGHT } from '../../Common/config/brandConfig';
import {
  MERCHANT_MANUAL_PAYMENT_ENABLED,
  MERCHANT_TERMINAL_REGISTRATION_ENABLED,
} from '../../Common/config/menuConfig';
import {
  createCpTerminal,
  getCpManualPayments,
  getCpManualPaymentTerminalSummary,
  getCpCustomerList,
  getCpTerminalList,
  updateCpManualPaymentPharmacyName,
  revealCpManualPaymentCard,
  sendCpManualPaymentReceiptEmail,
  sendCpManualPaymentReceiptAlimtalk,
  sendCpManualPaymentReceiptSms,
  terminalChangePassword,
  updateCpTerminal,
} from '../api/cp';
import { cpMe } from '../api/cpAuth';

dayjs.extend(utc);

const RECEIPT_SMS_ENABLED = Number(process.env.REACT_APP_RECEIPT_SMS_ENABLED ?? 0) === 1;
const RECEIPT_KAKAO_ENABLED = Number(process.env.REACT_APP_RECEIPT_KAKAO_ENABLED ?? 0) === 1;
const PHARMACY_ASSIGNMENT_ENABLED = ['EXCEL', 'SHINSUNG'].includes(
  String(process.env.REACT_APP_TERMINAL_PHARMACY_SOURCE || 'DISABLED').trim().toUpperCase(),
);

const HANGUL_REGEX = /[ㄱ-ㅎㅏ-ㅣ가-힣]/g;

function formatDateDot(d) {
  return dayjs(d).format('YYYY.MM.DD');
}

function normalizeDate(str) {
  const t = String(str || '').trim().replace(/\./g, '-');
  const dt = dayjs(t);
  return dt.isValid() ? dt : dayjs();
}

function toDateInput(str) {
  return normalizeDate(str).format('YYYY-MM-DD');
}

function toDateDot(str) {
  return normalizeDate(str).format('YYYY.MM.DD');
}

function toDateApi(str) {
  return normalizeDate(str).format('YYYY-MM-DD');
}

function toKstDateTime(str, endOfDay = false) {
  const time = endOfDay ? '23:59:59.999' : '00:00:00';
  return `${toDateApi(str)}T${time}+09:00`;
}

function formatWon(n) {
  return `${Number(n || 0).toLocaleString('ko-KR')}원`;
}

function stripHangul(value) {
  return String(value ?? '').replace(HANGUL_REGEX, '');
}

function formatPhone(value) {
  const nums = String(value ?? '').replace(/[^0-9]/g, '');
  if (nums.length <= 3) return nums;
  if (nums.startsWith('010')) {
    if (nums.length <= 7) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
    return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7, 11)}`;
  }
  if (nums.startsWith('02')) {
    if (nums.length <= 5) return `${nums.slice(0, 2)}-${nums.slice(2)}`;
    if (nums.length <= 9) return `${nums.slice(0, 2)}-${nums.slice(2, 5)}-${nums.slice(5, 9)}`;
    return `${nums.slice(0, 2)}-${nums.slice(2, 6)}-${nums.slice(6, 10)}`;
  }
  if (nums.length <= 6) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
  if (nums.length <= 10) return `${nums.slice(0, 3)}-${nums.slice(3, 6)}-${nums.slice(6, 10)}`;
  return `${nums.slice(0, 3)}-${nums.slice(3, 7)}-${nums.slice(7, 11)}`;
}

function formatBusinessNo(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '-';
  if (digits.length !== 10) return String(value);
  return digits.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3');
}

function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch {
    return '-';
  }
}

function mapPayment(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.payment_id ??
    item.paymentId ??
    item.id ??
    '';
  const transactionType = String(item.transaction_type ?? item.transactionType ?? '').toUpperCase();
  const transactionStatus = item.transaction_status ?? item.transactionStatus ?? '';
  const rawAmount = Number(item.amount ?? item.trdAmt ?? item.trd_amt ?? item.pg_trd_amt ?? 0);
  const rawFee = Number(item.cp_fee_amount ?? item.cpFeeAmount ?? item.fee_amount ?? item.fee ?? 0);
  const businessDate = item.business_created_at ?? item.businessCreatedAt;
  const canceledAt = item.canceled_at ?? item.canceledAt ?? null;
  const dateStr =
    businessDate ??
    (transactionType === 'CANCEL' ? canceledAt : null) ??
    item.approved_at ??
    item.approvedAt ??
    item.created_at ??
    item.createdAt ??
    '';
  const date = dateStr
    ? businessDate
      ? dayjs.utc(dateStr).subtract(9, 'hour').toISOString()
      : dayjs.utc(dateStr).toISOString()
    : '';
  const rowId =
    item.transaction_id ??
    item.transactionId ??
    [paymentTransactionId || 'payment', transactionType || 'UNKNOWN', dateStr].join(':');
  const instmtMon = String(item.instmt_mon ?? item.instmtMon ?? item.pg_instmt_mon ?? '00');
  const statusRaw = String(item.status ?? transactionStatus ?? '').toUpperCase();
  const isCancelTransaction = transactionType === 'CANCEL';
  const isCanceledApproval = !isCancelTransaction && (statusRaw === 'CANCELED' || canceledAt != null);
  const status = isCancelTransaction
    ? (statusRaw.includes('PENDING') ? '취소대기' : '취소')
    : isCanceledApproval
      ? '취소'
      : (statusRaw === 'COMPLETED' || statusRaw.includes('APPROVE') || statusRaw.includes('SUCCESS') ? '승인' : (item.status || transactionStatus || '-'));

  return {
    id: rowId,
    paymentTransactionId,
    date,
    approvalNo: item.appr_no ?? item.apprNo ?? item.approval_no ?? item.approvalNumber ?? item.original_appr_no ?? item.originalApprNo ?? '',
    pharmacy:
      item.transaction_pharmacy_name ??
      item.transactionPharmacyName ??
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy_name ??
      item.pharmacyName ??
      item.customer_name ??
      '-',
    cardName: item.card_nm ?? item.cardNm ?? item.cardName ?? parseCardNameFromRaw(item.pg_raw_response),
    pgProvider: item.pg_provider ?? item.pgProvider ?? '',
    installment: instmtMon === '00' ? '일시불' : `${instmtMon}개월`,
    amount: isCancelTransaction ? -Math.abs(rawAmount) : rawAmount,
    fee: isCancelTransaction ? -Math.abs(rawFee) : rawFee,
    driverName: item.salesperson_name ?? item.salespersonName ?? item.driver_name ?? item.driverName ?? item.terminal_name ?? item.terminalName ?? '',
    driverId: item.salesperson_id ?? item.salespersonId ?? item.terminal_id ?? item.terminalId ?? '',
    status,
    transactionType,
    transactionStatus,
    canceledAt,
    isCancelTransaction,
    isCanceledApproval,
    transactionKind: isCancelTransaction ? '취소건' : '원결제',
  };
}

function normalizeTerminalList(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  if (!Array.isArray(rows)) return [];
  return rows.map((row, idx) => {
    const pk = row.id ?? row.terminal_id;
    const numId = pk != null && pk !== '' ? Number(pk) : NaN;
    return {
      id: Number.isFinite(numId) ? numId : idx + 1,
      driverCode: row.code ?? row.terminal_code ?? row.driverCode ?? String(pk ?? idx + 1),
      driverId: row.terminalId != null ? String(row.terminalId) : (row.terminal_id != null ? String(row.terminal_id) : row.driverId ?? ''),
      name: row.name ?? '',
      phone: row.phone ?? '',
      status: String(row.status ?? 'ACTIVE').toUpperCase(),
    };
  });
}

function normalizeAssignedPharmacyList(data) {
  const rows = data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => ({
    id: row.mappingId ?? row.mapping_id ?? row.id ?? row.pharmacyId ?? row.pharmacy_id ?? index,
    pharmacyId: row.pharmacyId ?? row.pharmacy_id ?? row.id ?? '',
    code: row.externalCustomerCode ?? row.external_customer_code ?? row.institutionCode ?? row.institution_code ?? row.code ?? '',
    name: row.name ?? row.customerName ?? row.customer_name ?? row.pharmacyName ?? row.pharmacy_name ?? '',
    businessNo: row.businessNo ?? row.business_no ?? '',
    phone: row.phone ?? row.pharmacyPhone ?? row.pharmacy_phone ?? row.tel ?? '',
    address: row.address ?? row.addr ?? '',
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
  }));
}

function AssignedPharmacyTable({ rows, query, onQueryChange, loading, error }) {
  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b border-[#eef1f4] flex items-center gap-3">
        <div className="h-[34px] w-[300px] flex items-center rounded-lg border border-[#e5e8eb] bg-white px-3 focus-within:border-[var(--brand-primary)]">
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="거래처코드, 약국명, 사업자번호 검색"
            className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-[#191f28] outline-none"
          />
        </div>
        <span className="text-[12px] font-semibold text-[#8b95a1]">총 {rows.length.toLocaleString('ko-KR')}곳</span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 bg-[#f9fafb]">
            <tr className="border-b border-[#eef1f4] text-[#4e5968]">
              <th className="px-3 py-2 text-left w-[110px]">거래처코드</th>
              <th className="px-3 py-2 text-left">약국명</th>
              <th className="px-3 py-2 text-left w-[120px]">사업자번호</th>
              <th className="px-3 py-2 text-left w-[120px]">연락처</th>
              <th className="px-3 py-2 text-center w-[64px]">상태</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[#8b95a1]">담당 약국 조회 중...</td></tr>
            ) : error ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-red-500">{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-[#8b95a1]">할당된 약국이 없습니다.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="border-b border-[#f1f3f6] hover:bg-[#f8fafc]">
                <td className="px-3 py-2 font-mono whitespace-nowrap">{row.code || '-'}</td>
                <td className="px-3 py-2 font-semibold text-[#191f28] truncate" title={row.name || '-'}>{row.name || '-'}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatBusinessNo(row.businessNo)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{formatPhone(row.phone) || '-'}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {row.status === 'ACTIVE' ? '활성' : '비활성'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeliveryManage() {
  const { showToast } = useToast();
  const { showAllOpen, toggleShowAll } = useTooltip();
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  const cpSupplierInfo = useSelector((state) => state.cpSupplierInfo?.info);
  const cpPgMerchantRows = useSelector((state) => state.cpPgMerchantInfo?.rows) || [];
  const [cpInfo, setCpInfo] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [startDate, setStartDate] = useState(formatDateDot(dayjs().startOf('month')));
  const [endDate, setEndDate] = useState(formatDateDot(dayjs()));
  const [drivers, setDrivers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [terminalSummaryRows, setTerminalSummaryRows] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [driverDetailTab, setDriverDetailTab] = useState(PHARMACY_ASSIGNMENT_ENABLED ? 'pharmacies' : 'payments');
  const [assignedPharmacies, setAssignedPharmacies] = useState([]);
  const [assignedPharmacyQuery, setAssignedPharmacyQuery] = useState('');
  const [loadingAssignedPharmacies, setLoadingAssignedPharmacies] = useState(false);
  const [assignedPharmacyError, setAssignedPharmacyError] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [pwdNew, setPwdNew] = useState('');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalTarget, setStatusModalTarget] = useState(null);
  const [statusModalNext, setStatusModalNext] = useState(null);

  // 전표(신용카드 매출전표) 미리보기 모달
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [receiptPreviewData, setReceiptPreviewData] = useState(null);
  const [receiptViewMode, setReceiptViewMode] = useState('approval'); // 'approval' | 'cancel'
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
  const [receiptPhone, setReceiptPhone] = useState('');

  // 카드번호 조회 모달
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealReason, setRevealReason] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);

  // 거래 약국명 변경 모달
  const [pharmacyNameModalOpen, setPharmacyNameModalOpen] = useState(false);
  const [pharmacyNameTarget, setPharmacyNameTarget] = useState(null);
  const [pharmacyNameDraft, setPharmacyNameDraft] = useState('');
  const [pharmacyNameReason, setPharmacyNameReason] = useState('');
  const [pharmacyNameSaving, setPharmacyNameSaving] = useState(false);
  const pharmacyNameInputRef = useRef(null);

  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);

  useEffect(() => {
    if (!pharmacyNameModalOpen) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      pharmacyNameInputRef.current?.focus();
      pharmacyNameInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [pharmacyNameModalOpen]);

  // 페이지 진입 시 CP(가맹점) 기본정보 1회 조회
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cp = await cpMe();
        if (!cancelled) setCpInfo(cp || null);
      } catch {
        if (!cancelled) setCpInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const [registerForm, setRegisterForm] = useState({
    terminalId: '',
    tempPassword: '',
    name: '',
    phone: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
  });

  const setQuickPeriod = (type) => {
    const today = dayjs();
    if (type === '당일') {
      setStartDate(formatDateDot(today));
      setEndDate(formatDateDot(today));
    } else if (type === '전일') {
      const nextStart = normalizeDate(startDate).subtract(1, 'day');
      const nextEnd = normalizeDate(endDate).subtract(1, 'day');
      setStartDate(formatDateDot(nextStart));
      setEndDate(formatDateDot(nextEnd));
    } else if (type === '당월') {
      setStartDate(formatDateDot(today.startOf('month')));
      setEndDate(formatDateDot(today));
    } else if (type === '전월') {
      const prevMonth = normalizeDate(startDate).subtract(1, 'month');
      setStartDate(formatDateDot(prevMonth.startOf('month')));
      setEndDate(formatDateDot(prevMonth.endOf('month')));
    }
  };

  const fetchDrivers = useCallback(async () => {
    try {
      setLoadingDrivers(true);
      const data = await getCpTerminalList({ page: 1, limit: 500 });
      const list = normalizeTerminalList(data);
      setDrivers(list);
      setSelectedDriverId((prev) => (prev && list.some((d) => d.id === prev) ? prev : (list[0]?.id ?? null)));
    } catch (e) {
      showToast(e.message || '영업자 목록을 불러오지 못했습니다.', 'error');
      setDrivers([]);
      setSelectedDriverId(null);
    } finally {
      setLoadingDrivers(false);
    }
  }, [showToast]);

  const terminalIdForPayments = useMemo(() => {
    const d = drivers.find((x) => x.id === selectedDriverId);
    return d?.id;
  }, [drivers, selectedDriverId]);

  const fetchPayments = useCallback(async (searchKeyword = '') => {
    try {
      setLoadingPayments(true);
      const dateFromKST = toKstDateTime(startDate);
      const dateToKST = toKstDateTime(endDate, true);
      const params = {
        page: 1,
        limit: 1000,
        dateFrom: dateFromKST,
        dateTo: dateToKST,
      };
      const kw = String(searchKeyword || '').trim();
      if (kw) params.q = kw;
      if (terminalIdForPayments != null) params.terminalId = terminalIdForPayments;
      const data = await getCpManualPayments(params);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const filtered = rows.filter((r) => String(r.status || '').toUpperCase() !== 'FAILED');
      setPayments(filtered.map(mapPayment));
    } catch (e) {
      setPayments([]);
      showToast(e.message || '결제내역을 불러오지 못했습니다.', 'error');
    } finally {
      setLoadingPayments(false);
    }
  }, [startDate, endDate, terminalIdForPayments, showToast]);

  const fetchTerminalSummary = useCallback(async () => {
    try {
      const summaryDateFrom = toKstDateTime(startDate);
      const summaryDateTo = toKstDateTime(endDate, true);
      const data = await getCpManualPaymentTerminalSummary({
        dateFrom: summaryDateFrom,
        dateTo: summaryDateTo,
      });
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.list)
          ? data.list
          : Array.isArray(data)
            ? data
            : [];
      setTerminalSummaryRows(rows);
    } catch (e) {
      setTerminalSummaryRows([]);
      showToast(e.message || '영업자 기간별 매출 요약을 불러오지 못했습니다.', 'error');
    }
  }, [startDate, endDate, showToast]);

  // 기간이 변경되면: 영업자 목록 + 기간요약 조회
  useEffect(() => {
    fetchDrivers();
    fetchTerminalSummary();
  }, [startDate, endDate]);

  // 영업자 선택 또는 기간 변경 시 결제내역 재조회 (영업자가 선택된 경우에만)
  // fetchPayments는 startDate/endDate/terminalIdForPayments 변경 시 재생성되므로
  // 초기 마운트(terminalId 미확정) 시점의 전체 조회를 방지함
  useEffect(() => {
    if (terminalIdForPayments == null) return;
    fetchPayments(keyword);
  }, [fetchPayments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!PHARMACY_ASSIGNMENT_ENABLED || terminalIdForPayments == null) {
      setAssignedPharmacies([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingAssignedPharmacies(true);
      setAssignedPharmacyError('');
      try {
        const params = { terminalId: terminalIdForPayments, page: 1, limit: 1000 };
        const q = assignedPharmacyQuery.trim();
        if (q) params.q = q;
        const data = await getCpCustomerList(params);
        if (!cancelled) setAssignedPharmacies(normalizeAssignedPharmacyList(data));
      } catch (e) {
        if (!cancelled) {
          setAssignedPharmacies([]);
          setAssignedPharmacyError(e?.message || '담당 약국을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) setLoadingAssignedPharmacies(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [terminalIdForPayments, assignedPharmacyQuery]);

  const matchDriverPayment = useCallback((driver, tx) => {
    const keys = [tx.driverName, tx.driverId].map((v) => String(v || '').toLowerCase());
    const targets = [driver.driverId, driver.name, driver.driverCode].map((v) => String(v || '').toLowerCase());
    return targets.some((t) => t && keys.some((k) => k.includes(t) || t.includes(k)));
  }, []);

  const findSummaryForDriver = useCallback((driver) => {
    const driverPk = String(driver?.id ?? '').trim();
    if (!driverPk) return null;
    return terminalSummaryRows.find((row) => String(row?.terminal_id ?? row?.terminalId ?? '').trim() === driverPk) || null;
  }, [terminalSummaryRows]);

  const driversWithStats = useMemo(() => {
    return drivers.map((d) => {
      const summary = findSummaryForDriver(d);
      const periodSales = Number(
        summary?.completed_amount ??
        summary?.completedAmount ??
        0
      );
      const periodCount = Number(
        summary?.completed_count ??
        summary?.completedCount ??
        0
      );
      return { ...d, periodSales, periodCount };
    });
  }, [drivers, findSummaryForDriver]);

  const filteredDrivers = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return driversWithStats;
    return driversWithStats.filter((d) =>
      [d.driverCode, d.driverId, d.name, d.phone].some((v) => String(v || '').toLowerCase().includes(kw)),
    );
  }, [driversWithStats, keyword]);

  const selectedDriver = useMemo(
    () => filteredDrivers.find((d) => d.id === selectedDriverId) || filteredDrivers[0] || null,
    [filteredDrivers, selectedDriverId],
  );

  const rightRows = useMemo(() => {
    if (!selectedDriver) return [];
    return payments.filter((tx) => matchDriverPayment(selectedDriver, tx));
  }, [payments, selectedDriver, matchDriverPayment]);

  const openPharmacyNameModal = useCallback((row) => {
    const paymentTransactionId = row?.paymentTransactionId;
    if (!paymentTransactionId) {
      showToast('거래 ID가 없어 약국명을 수정할 수 없습니다.', 'error');
      return;
    }

    setPharmacyNameTarget(row);
    setPharmacyNameDraft(row.pharmacy === '-' ? '' : String(row.pharmacy || ''));
    setPharmacyNameReason('');
    setPharmacyNameModalOpen(true);
  }, [showToast]);

  const closePharmacyNameModal = useCallback(() => {
    if (pharmacyNameSaving) return;
    setPharmacyNameModalOpen(false);
    setPharmacyNameTarget(null);
    setPharmacyNameDraft('');
    setPharmacyNameReason('');
  }, [pharmacyNameSaving]);

  const handlePharmacyNameSave = useCallback(async () => {
    if (!pharmacyNameTarget || pharmacyNameSaving) return;

    const paymentTransactionId = pharmacyNameTarget.paymentTransactionId;
    const nextPharmacyName = pharmacyNameDraft.trim();
    const reason = pharmacyNameReason.trim();

    if (!paymentTransactionId) {
      showToast('거래 ID가 없어 약국명을 수정할 수 없습니다.', 'error');
      return;
    }

    if (!nextPharmacyName) {
      showToast('약국명을 입력해 주세요.', 'error');
      pharmacyNameInputRef.current?.focus();
      return;
    }

    if (nextPharmacyName.length > 100) {
      showToast('약국명은 최대 100자까지 입력할 수 있습니다.', 'error');
      pharmacyNameInputRef.current?.focus();
      return;
    }

    if (reason.length > 500) {
      showToast('변경 사유는 최대 500자까지 입력할 수 있습니다.', 'error');
      return;
    }

    const currentPharmacyName = pharmacyNameTarget.pharmacy === '-' ? '' : String(pharmacyNameTarget.pharmacy || '').trim();
    if (nextPharmacyName === currentPharmacyName) {
      closePharmacyNameModal();
      return;
    }

    setPharmacyNameSaving(true);
    try {
      await updateCpManualPaymentPharmacyName({
        id: paymentTransactionId,
        pharmacyName: nextPharmacyName,
        reason,
      });
      showToast('약국명이 변경되었습니다.');
      setPharmacyNameModalOpen(false);
      setPharmacyNameTarget(null);
      setPharmacyNameDraft('');
      setPharmacyNameReason('');
      await fetchPayments(keyword);
    } catch (e) {
      showToast(e.message || '약국명 변경에 실패했습니다.', 'error');
    } finally {
      setPharmacyNameSaving(false);
    }
  }, [closePharmacyNameModal, fetchPayments, keyword, pharmacyNameDraft, pharmacyNameReason, pharmacyNameSaving, pharmacyNameTarget, showToast]);

  const downloadDriverExcel = () => {
    const header = ['영업자코드', 'ID', '이름', '연락처', '상태', '기간별 매출', '기간별 건수'];
    const lines = filteredDrivers.map((d) =>
      [d.driverCode, d.driverId, d.name, d.phone, d.status === 'ACTIVE' ? '활성' : '비활성', d.periodSales, d.periodCount]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = '\uFEFF' + header.map((h) => `"${h}"`).join(',') + '\r\n' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `영업자목록_${dayjs().format('YYYYMMDD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadPaymentExcel = () => {
    const header = ['결제일', '구분', '승인번호', '약국', '카드사', '할부', '금액', '수수료', '영업자', '상태'];
    const lines = rightRows.map((row) =>
      [
        row.date ? dayjs.utc(row.date).add(9, 'hour').format('YYYY.MM.DD. HH:mm') : '-',
        row.transactionKind || '-',
        row.approvalNo || '-',
        row.pharmacy || '-',
        row.cardName || '-',
        row.installment || '-',
        Number(row.amount || 0),
        Number(row.fee || 0),
        row.driverName || row.driverId || '-',
        row.status || '-',
      ]
        .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
        .join(','),
    );
    const csv = '\uFEFF' + header.map((h) => `"${h}"`).join(',') + '\r\n' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `영업자결제내역_${dayjs().format('YYYYMMDD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const RECEIPT_SUPPLIER_INFO = useMemo(() => ({
    supplierName: '-',
    supplierOwnerName: '-',
    supplierBusinessNo: '-',
    supplierContact: '-',
    supplierAddress: '-',
  }), []);

  const RECEIPT_CARD_MERCHANT_INFO = useMemo(() => ({
    cardMerchantName: '-',
    cardMerchantOwnerName: '-',
    cardMerchantBusinessNo: '-',
    cardMerchantContact: '-',
    cardMerchantAddress: '-',
  }), []);

  const receiptSupplierInfo = useMemo(
    () => ({
      supplierName: cpSupplierInfo?.name || RECEIPT_SUPPLIER_INFO.supplierName,
      supplierOwnerName: cpSupplierInfo?.owner_name || RECEIPT_SUPPLIER_INFO.supplierOwnerName,
      supplierBusinessNo: cpSupplierInfo?.business_no || RECEIPT_SUPPLIER_INFO.supplierBusinessNo,
      supplierContact: cpSupplierInfo?.contact || RECEIPT_SUPPLIER_INFO.supplierContact,
      supplierAddress: cpSupplierInfo?.address || RECEIPT_SUPPLIER_INFO.supplierAddress,
    }),
    [cpSupplierInfo, RECEIPT_SUPPLIER_INFO],
  );

  const getReceiptCardMerchantInfo = useCallback(
    (pgProvider) => {
      const provider = String(pgProvider || '').toUpperCase();
      const matched = cpPgMerchantRows.find(
        (row) => String(row?.pg_provider || '').toUpperCase() === provider,
      );
      if (!matched) return RECEIPT_CARD_MERCHANT_INFO;
      return {
        cardMerchantName: matched.name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantName,
        cardMerchantOwnerName: matched.owner_name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantOwnerName,
        cardMerchantBusinessNo: matched.business_no || RECEIPT_CARD_MERCHANT_INFO.cardMerchantBusinessNo,
        cardMerchantContact: matched.contact || matched.phone || RECEIPT_CARD_MERCHANT_INFO.cardMerchantContact,
        cardMerchantAddress: matched.address || RECEIPT_CARD_MERCHANT_INFO.cardMerchantAddress,
      };
    },
    [cpPgMerchantRows, RECEIPT_CARD_MERCHANT_INFO],
  );

  const formatKST = (dateValue, format = 'YYYY.MM.DD. HH:mm:ss') => {
    if (!dateValue) return '-';
    return dayjs.utc(dateValue).add(9, 'hour').format(format);
  };

  const openReceiptPreview = useCallback((row) => {
    if (!row) return;

    const amount = Math.abs(Number(row.amount || 0));
    const taxableAmount = Math.round(amount / 1.1);
    const vatAmount = amount - taxableAmount;
    const taxFreeAmount = 0;
    const serviceAmount = 0;

    const cardNumberMasked = '****-****-****-****';
    const approvalDateTime = row.date ? formatKST(row.date) : '-';
    const isCanceled = row.isCancelTransaction || row.isCanceledApproval;
    const cancelDateTime = row.canceledAt
      ? formatKST(row.canceledAt)
      : (row.isCancelTransaction ? approvalDateTime : null);
    const receiptCardMerchantInfo = getReceiptCardMerchantInfo(row.pgProvider);

    setReceiptPreviewData({
      paymentTransactionId: row.paymentTransactionId || row.id || '',
      cardBrand: row.cardName || '-',
      cardNumberMasked,
      installmentMonth: row.installment || '일시불',
      approvalNumber: row.approvalNo || '-',
      approvalDateTime,
      cancelDateTime,
      isCanceled,
      pharmacyName: row.pharmacy || '-',

      taxableAmount: `${taxableAmount.toLocaleString('ko-KR')}원`,
      vatAmount: `${vatAmount.toLocaleString('ko-KR')}원`,
      taxFreeAmount: `${taxFreeAmount.toLocaleString('ko-KR')}원`,
      serviceAmount: `${serviceAmount.toLocaleString('ko-KR')}원`,
      totalAmount: `${amount.toLocaleString('ko-KR')}원`,

      ...receiptSupplierInfo,
      merchantName: cpInfo?.name || '-',
      merchantOwnerName: cpInfo?.boss_name || '-',
      merchantBusinessNo: cpInfo?.business_no || '-',
      merchantContact: cpInfo?.phone || '-',
      merchantAddress: cpInfo?.address || '-',
      ...receiptCardMerchantInfo,
    });

    setReceiptViewMode(row.isCancelTransaction ? 'cancel' : 'approval');
    setReceiptEmail('');
    setRevealModalOpen(false);
    setRevealPassword('');
    setRevealReason('');
    setReceiptModalOpen(true);
  }, [receiptSupplierInfo, cpInfo, getReceiptCardMerchantInfo]);

  const handleRevealCardNumber = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.');
      return;
    }

    const password = (revealPassword || '').trim();
    if (!password) {
      showToast('비밀번호를 입력해 주세요.', 'error');
      return;
    }

    const reason = (revealReason || '').trim();
    if (!reason) {
      showToast('조회 사유를 입력해 주세요.', 'error');
      return;
    }

    setRevealLoading(true);
    try {
      const data = await revealCpManualPaymentCard({
        paymentTransactionId: receiptPreviewData.paymentTransactionId,
        password,
        reason,
      });

      const masked = data?.card_no_masked;
      if (!masked) throw new Error('카드번호를 가져오지 못했습니다.');

      setReceiptPreviewData((prev) => (prev ? { ...prev, cardNumberMasked: masked } : prev));
      setRevealModalOpen(false);
      setRevealPassword('');
      setRevealReason('');
      showToast('카드번호가 조회되었습니다.');
    } catch (e) {
      showToast(e.message || '카드번호 조회에 실패했습니다.', 'error');
    } finally {
      setRevealLoading(false);
    }
  }, [receiptPreviewData, revealPassword, revealReason, showToast]);

  const handleConfirmSendReceipt = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const closeReceipt = () => {
      setReceiptModalOpen(false);
      setReceiptEmail('');
      setReceiptPhone('');
      setSendMethod('email');
      setReceiptPreviewData(null);
      setReceiptViewMode('approval');
    };

    if (sendMethod === 'email') {
      const email = (receiptEmail || '').trim();
      if (!email) {
        showToast('전송할 이메일을 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        await sendCpManualPaymentReceiptEmail({
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          payment_transaction_id: receiptPreviewData.paymentTransactionId,
          email,
        });
        showToast('이메일로 전표가 전송되었습니다.');
        closeReceipt();
      } catch (e) {
        showToast(e.message || '전표 이메일 전송에 실패했습니다.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'sms') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        await sendCpManualPaymentReceiptSms({
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          phone,
        });
        showToast('전표 문자가 전송되었습니다.');
        closeReceipt();
      } catch (e) {
        showToast('관리자에게 문의해주세요.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'kakao') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        await sendCpManualPaymentReceiptAlimtalk({
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          phone,
        });
        showToast('전표 알림톡을 발송했습니다.');
        closeReceipt();
      } catch (e) {
        showToast('관리자에게 문의해주세요.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    }
  }, [sendMethod, receiptEmail, receiptPhone, receiptPreviewData, showToast]);

  const handleRegister = async () => {
    if (!MERCHANT_TERMINAL_REGISTRATION_ENABLED) {
      showToast('이 가맹점에서는 영업자를 등록할 수 없습니다.', 'error');
      return;
    }
    const name = registerForm.name.trim();
    const phone = registerForm.phone.replace(/[^0-9]/g, '');
    const tempPassword = registerForm.tempPassword.trim();
    const terminalIdOpt = stripHangul(registerForm.terminalId).trim();
    if (!name || !phone || !tempPassword) {
      showToast('이름, 연락처, 임시비밀번호는 필수입니다.', 'error');
      return;
    }
    try {
      await createCpTerminal({
        name,
        phone,
        tempPassword,
        ...(terminalIdOpt ? { terminalId: terminalIdOpt } : {}),
      });
      showToast('영업자가 등록되었습니다.');
      setRegisterOpen(false);
      setRegisterForm({ terminalId: '', tempPassword: '', name: '', phone: '' });
      fetchDrivers();
    } catch (e) {
      showToast(e.message || '영업자 등록에 실패했습니다.', 'error');
    }
  };

  const openEdit = (driver) => {
    setEditTarget(driver);
    setEditForm({ name: driver.name || '', phone: formatPhone(driver.phone || '') });
    setEditOpen(true);
  };

  const openPassword = (driver) => {
    setPwdTarget(driver);
    setPwdNew('');
    setPwdOpen(true);
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    const name = editForm.name.trim();
    const phone = editForm.phone.replace(/[^0-9]/g, '');
    if (!name || !phone) {
      showToast('이름과 연락처를 입력해 주세요.', 'error');
      return;
    }
    try {
      await updateCpTerminal({
        id: editTarget.id,
        name,
        phone,
      });
      showToast('영업자 정보가 수정되었습니다.');
      setEditOpen(false);
      setEditTarget(null);
      fetchDrivers();
    } catch (e) {
      showToast(e.message || '수정에 실패했습니다.', 'error');
    }
  };

  const handlePasswordSave = async () => {
    if (!pwdTarget) return;
    const newPassword = pwdNew.trim();
    if (!newPassword) {
      showToast('새 비밀번호를 입력해 주세요.', 'error');
      return;
    }
    try {
      await terminalChangePassword({ id: pwdTarget.id, newPassword });
      showToast('비밀번호가 변경되었습니다.');
      setPwdOpen(false);
      setPwdTarget(null);
      setPwdNew('');
    } catch (e) {
      showToast(e.message || '비밀번호 변경에 실패했습니다.', 'error');
    }
  };

  const openStatusModal = (driver) => {
    setStatusModalTarget(driver);
    setStatusModalNext(driver.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setStatusModalTarget(null);
    setStatusModalNext(null);
  };

  const confirmStatusChange = async () => {
    if (!statusModalTarget || !statusModalNext) return;
    try {
      await updateCpTerminal({ id: statusModalTarget.id, status: statusModalNext });
      showToast(statusModalNext === 'ACTIVE' ? '활성화되었습니다.' : '비활성화되었습니다.');
      closeStatusModal();
      fetchDrivers();
    } catch (e) {
      showToast(e.message || '상태 변경에 실패했습니다.', 'error');
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <Tooltip id="dm-search-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-search'} setIsOpen={() => {}} />
      <Tooltip id="dm-period-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-period'} setIsOpen={() => {}} />
      <Tooltip id="dm-drivers-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-drivers'} setIsOpen={() => {}} />
      <Tooltip id="dm-payments-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[320px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-payments'} setIsOpen={() => {}} />
      <Tooltip id="dm-excel-drivers-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-excel-drivers'} setIsOpen={() => {}} />
      <Tooltip id="dm-excel-payments-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-excel-payments'} setIsOpen={() => {}} />
      <Tooltip id="dm-icon-edit-tooltip" place="top" positionStrategy="absolute" className="max-w-[280px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-icon-edit'} setIsOpen={() => {}} />
      <Tooltip id="dm-icon-password-tooltip" place="top" positionStrategy="absolute" className="max-w-[280px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-icon-password'} setIsOpen={() => {}} />
      <Tooltip id="dm-icon-power-tooltip" place="top" positionStrategy="absolute" className="max-w-[280px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'dm-icon-power'} setIsOpen={() => {}} />

      <div className="flex-1 min-h-0 py-4 px-8 flex flex-col gap-3 overflow-auto">
      <div className="bg-white rounded-xl shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          <div
            data-tooltip-id="dm-search-tooltip"
            data-tooltip-html="약국명으로 결제내역을 검색합니다.<br/>검색 후 선택된 영업자 기준으로 우측 결제내역이 갱신됩니다."
            onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-search')}
            onMouseLeave={() => setHoveredTooltipId(null)}
            className="h-[40px] flex items-center gap-2 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[var(--brand-primary)] transition-all shrink-0 w-[280px] cursor-help"
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchPayments(keyword); }}
              placeholder="약국명 검색"
              className="bg-transparent border-none outline-none text-[14px] text-[#191f28] py-0 h-full flex-1 min-w-0"
            />
            <button
              type="button"
              onClick={() => fetchPayments(keyword)}
              className="px-2 py-1 rounded bg-[var(--brand-primary)] text-white text-[12px] font-bold hover:opacity-90 shrink-0 cursor-pointer"
            >
              검색
            </button>
          </div>
          <div className="h-4 w-[1px] bg-[#e5e8eb]" />
          <div
            data-tooltip-id="dm-period-tooltip"
            data-tooltip-html="결제내역 조회 기간입니다.<br/>시작일·종료일을 바꾸면 자동으로 다시 조회됩니다.<br/>당일·전일·당월·전월 버튼으로 빠르게 설정할 수 있습니다."
            onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-period')}
            onMouseLeave={() => setHoveredTooltipId(null)}
            className="h-[40px] flex items-center gap-1.5 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[var(--brand-primary)] transition-all cursor-help"
          >
            <input
              type="date"
              value={toDateInput(startDate)}
              onChange={(e) => setStartDate(toDateDot(e.target.value))}
              className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
            />
            <span className="text-[#8b95a1] text-[12px]">~</span>
            <input
              type="date"
              value={toDateInput(endDate)}
              onChange={(e) => setEndDate(toDateDot(e.target.value))}
              className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
            />
          </div>
          <div className="flex gap-1">
            {['당일', '전일', '당월', '전월'].map((type) => (
              <button
                key={type}
                onClick={() => setQuickPeriod(type)}
                className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[40px] flex items-center"
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex gap-4">
        <section className="basis-[45%] min-w-0 bg-white rounded-xl border border-[#e5e8eb] flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-[#eef1f4] flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {MERCHANT_TERMINAL_REGISTRATION_ENABLED && (
                <button onClick={() => setRegisterOpen(true)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[var(--brand-primary)] text-white rounded-lg font-bold text-[14px] hover:opacity-90 transition-colors shrink-0"><Plus size={14} />영업자 등록</button>
              )}
              <span
                data-tooltip-id="dm-drivers-tooltip"
                data-tooltip-html="영업자(계정) 목록입니다.<br/>행을 클릭하면 해당 영업자의 결제내역이 우측에 표시됩니다.<br/>관리 아이콘으로 정보 수정·비밀번호 변경·활성/비활성을 할 수 있습니다."
                onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-drivers')}
                onMouseLeave={() => setHoveredTooltipId(null)}
                className="inline-flex cursor-help text-[#8b95a1] hover:text-[#4e5968] shrink-0"
              >
                <HelpCircle size={14} strokeWidth={2} />
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={downloadDriverExcel}
                data-tooltip-id="dm-excel-drivers-tooltip"
                data-tooltip-html="현재 목록에 보이는 영업자 정보를 CSV로 내려받습니다.<br/>코드, ID, 이름, 연락처, 상태, 기간별 매출·건수가 포함됩니다."
                onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-excel-drivers')}
                onMouseLeave={() => setHoveredTooltipId(null)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors cursor-help"
              >
                <Download size={14} />엑셀다운로드
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[#f9fafb] z-10">
                <tr className="text-[#4e5968] border-b border-[#eef1f4]">
                  <th className="px-3 py-1 text-left w-[50px]">코드</th>
                  <th className="px-3 py-1 text-left w-[120px]">ID</th>
                  <th className="px-3 py-1 text-left w-[90px]">이름</th>
                  <th className="px-3 py-1 text-left w-[120px]">연락처</th>
                  <th className="px-3 py-1 text-center w-[56px]">상태</th>
                  <th className="px-3 py-1 text-right w-[120px]">기간별 매출</th>
                  <th className="px-3 py-1 text-right w-[90px]">기간별 건수</th>
                  <th className="px-2 py-1 text-center w-[120px]">관리</th>
                </tr>
              </thead>
              <tbody>
                {loadingDrivers ? (
                  <tr><td className="px-3 py-6 text-center text-[#8b95a1]" colSpan={9}>불러오는 중...</td></tr>
                ) : filteredDrivers.length === 0 ? (
                  <tr><td className="px-3 py-6 text-center text-[#8b95a1]" colSpan={9}>영업자가 없습니다.</td></tr>
                ) : filteredDrivers.map((d) => (
                  <tr key={d.id} className={`border-b border-[#f1f3f6] cursor-pointer ${d.status === 'INACTIVE' ? 'opacity-75' : ''} ${selectedDriver?.id === d.id ? 'bg-[#eef5ff]' : 'hover:bg-[#f8fafc]'}`} onClick={() => setSelectedDriverId(d.id)}>
                    <td className="px-3 py-1 w-[50px] whitespace-nowrap">{d.driverCode || '-'}</td>
                    <td className="px-3 py-1 font-mono w-[120px] whitespace-nowrap">{d.driverId || '-'}</td>
                    <td className="px-3 py-1 w-[90px] whitespace-nowrap truncate">{d.name || '-'}</td>
                    <td className="px-3 py-1 w-[120px] whitespace-nowrap">{formatPhone(d.phone)}</td>
                    <td className="px-3 py-1 text-center w-[56px] whitespace-nowrap">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${d.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {d.status === 'ACTIVE' ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="px-3 py-1 text-right tabular-nums w-[120px]">{formatWon(d.periodSales)}</td>
                    <td className="px-3 py-1 text-right tabular-nums w-[90px]">{Number(d.periodCount || 0).toLocaleString('ko-KR')}</td>
                    <td className="px-2 py-1 text-center w-[120px]" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(d)}
                          data-tooltip-id="dm-icon-edit-tooltip"
                          data-tooltip-html="영업자 정보 수정<br/>이름·연락처를 변경할 수 있습니다."
                          onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-icon-edit')}
                          onMouseLeave={() => setHoveredTooltipId(null)}
                          className="p-1.5 rounded-lg border border-[#e5e8eb] text-[var(--brand-primary)] bg-white hover:bg-[var(--brand-primary-soft)] transition-colors cursor-help"
                        >
                          <Pencil size={14} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openPassword(d)}
                          data-tooltip-id="dm-icon-password-tooltip"
                          data-tooltip-html="로그인 비밀번호 변경<br/>해당 영업자의 새 비밀번호를 설정합니다."
                          onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-icon-password')}
                          onMouseLeave={() => setHoveredTooltipId(null)}
                          className="p-1.5 rounded-lg border border-[#e5e8eb] text-[#191f28] bg-white hover:bg-[#f2f4f6] transition-colors cursor-help"
                        >
                          <KeyRound size={14} strokeWidth={2} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openStatusModal(d)}
                          data-tooltip-id="dm-icon-power-tooltip"
                          data-tooltip-html={
                            d.status === 'ACTIVE'
                              ? '비활성화<br/>해당 영업자는 로그인·업무 처리가 제한될 수 있습니다.'
                              : '활성화<br/>비활성화된 영업자 계정을 다시 사용할 수 있게 합니다.'
                          }
                          onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-icon-power')}
                          onMouseLeave={() => setHoveredTooltipId(null)}
                          className={`p-1.5 rounded-lg border border-[#e5e8eb] bg-white transition-colors cursor-help ${d.status === 'ACTIVE' ? 'text-amber-600 hover:bg-amber-50' : 'text-emerald-600 hover:bg-emerald-50'}`}
                        >
                          <Power size={14} strokeWidth={2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {MERCHANT_MANUAL_PAYMENT_ENABLED && (
              <div className="px-3 pt-4 text-[13px] font-semibold text-[#4e5968]">
                *가맹점 직접결제건은 영업자화면에서 확인할 수 없습니다.
              </div>
            )}
          </div>
        </section>

        <section className="basis-[55%] min-w-0 bg-white rounded-xl border border-[#e5e8eb] flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-[#eef1f4] flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              {PHARMACY_ASSIGNMENT_ENABLED && (
                <div className="flex rounded-lg bg-[#f2f4f6] p-0.5 shrink-0">
                  <button type="button" onClick={() => setDriverDetailTab('pharmacies')} className={`px-2.5 py-1 rounded-md text-[12px] font-bold transition-colors ${driverDetailTab === 'pharmacies' ? 'bg-white text-[var(--brand-primary)] shadow-sm' : 'text-[#6b7684]'}`}>담당 약국</button>
                  <button type="button" onClick={() => setDriverDetailTab('payments')} className={`px-2.5 py-1 rounded-md text-[12px] font-bold transition-colors ${driverDetailTab === 'payments' ? 'bg-white text-[var(--brand-primary)] shadow-sm' : 'text-[#6b7684]'}`}>결제내역</button>
                </div>
              )}
              <h2 className="text-[16px] font-bold text-[#191f28] truncate">
                {driverDetailTab === 'pharmacies' ? '담당 약국' : '결제내역'} {selectedDriver ? `(${selectedDriver.name || selectedDriver.driverId || '-'})` : ''}
              </h2>
              <span
                data-tooltip-id="dm-payments-tooltip"
                data-tooltip-html="선택한 영업자의 수기결제 내역입니다.<br/>전표 버튼으로 매출전표를 확인·이메일 전송할 수 있습니다."
                onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-payments')}
                onMouseLeave={() => setHoveredTooltipId(null)}
                className="inline-flex cursor-help text-[#8b95a1] hover:text-[#4e5968] shrink-0"
              >
                <HelpCircle size={14} strokeWidth={2} />
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* <span className="text-[12px] text-[#8b95a1]">조회기간 {startDate} ~ {endDate}</span> */}
              <button
                type="button"
                onClick={downloadPaymentExcel}
                data-tooltip-id="dm-excel-payments-tooltip"
                data-tooltip-html="선택된 영업자의 결제내역을 CSV로 내려받습니다.<br/>결제일, 승인번호, 약국, 카드사, 금액 등이 포함됩니다."
                onMouseEnter={() => !showAllOpen && setHoveredTooltipId('dm-excel-payments')}
                onMouseLeave={() => setHoveredTooltipId(null)}
                className={`${PHARMACY_ASSIGNMENT_ENABLED && driverDetailTab === 'pharmacies' ? 'hidden ' : ''}inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors cursor-help`}
              >
                <Download size={14} />엑셀다운로드
              </button>
            </div>
          </div>
          {PHARMACY_ASSIGNMENT_ENABLED && driverDetailTab === 'pharmacies' && (
            <AssignedPharmacyTable
              rows={assignedPharmacies}
              query={assignedPharmacyQuery}
              onQueryChange={setAssignedPharmacyQuery}
              loading={loadingAssignedPharmacies}
              error={assignedPharmacyError}
            />
          )}
          <div className={`${PHARMACY_ASSIGNMENT_ENABLED && driverDetailTab === 'pharmacies' ? 'hidden ' : ''}flex-1 min-h-0 overflow-auto`}>
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[#f9fafb] z-10">
                <tr className="text-[#4e5968] border-b border-[#eef1f4]">
                  <th className="px-3 py-1 text-left w-[130px]">결제일</th>
                  <th className="px-3 py-1 text-left w-[90px]">승인번호</th>
                  <th className="px-3 py-1 text-left w-[130px]">약국</th>
                  <th className="px-3 py-1 text-left w-[90px]">카드사</th>
                  <th className="px-3 py-1 text-center w-[56px]">할부</th>
                  <th className="px-3 py-1 text-right w-[105px]">금액</th>
                  <th className="px-3 py-1 text-right w-[95px]">수수료</th>
                  <th className="px-3 py-1 text-left w-[90px]">영업자</th>
                  <th className="px-3 py-1 text-center w-[72px]">구분</th>
                  <th className="px-3 py-1 text-center w-[62px]">전표</th>
                </tr>
              </thead>
              <tbody>
                {loadingPayments ? (
                  <tr><td className="px-3 py-6 text-center text-[#8b95a1]" colSpan={10}>결제내역 조회 중...</td></tr>
                ) : rightRows.length === 0 ? (
                  <tr><td className="px-3 py-6 text-center text-[#8b95a1]" colSpan={10}>결제내역이 없습니다.</td></tr>
                ) : rightRows.map((row) => (
                  <tr key={row.id || `${row.approvalNo}-${row.date}`} className="border-b border-[#f1f3f6]">
                    {(() => {
                      const isCancelTransaction = row.isCancelTransaction;
                      const canceledApproval = row.isCanceledApproval;
                      const rowStateClass = isCancelTransaction
                        ? ' bg-[#fef2f2]'
                        : canceledApproval
                          ? ' bg-[#f1f3f5]'
                          : '';
                      return (
                        <>
                          <td className={`px-3 py-1 w-[130px] whitespace-nowrap${rowStateClass}`}>{row.date ? dayjs.utc(row.date).add(9, 'hour').format('YYYY.MM.DD. HH:mm') : '-'}</td>
                          <td className={`px-3 py-1 w-[90px] whitespace-nowrap${rowStateClass}`}>{row.approvalNo || '-'}</td>
                          <td className={`px-3 py-1 w-[130px]${rowStateClass}`}>
                            <div className="flex w-[130px] min-w-0 items-center gap-1 overflow-hidden">
                              <span className="min-w-0 flex-1 truncate" title={row.pharmacy || '-'}>{row.pharmacy || '-'}</span>
                              <button
                                type="button"
                                title="약국명 수정"
                                aria-label={`${row.pharmacy || '약국'} 약국명 수정`}
                                disabled={pharmacyNameSaving || !row.paymentTransactionId}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPharmacyNameModal(row);
                                }}
                                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-[#8b95a1] hover:bg-[#f2f4f6] hover:text-[var(--brand-primary)] disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <Pencil size={12} strokeWidth={2} />
                              </button>
                            </div>
                          </td>
                          <td className={`px-3 py-1 w-[90px] whitespace-nowrap${rowStateClass}`}>{row.cardName || '-'}</td>
                          <td className={`px-3 py-1 text-center w-[56px] whitespace-nowrap${rowStateClass}`}>{row.installment || '-'}</td>
                          <td className={`px-3 py-1 text-right tabular-nums w-[105px]${rowStateClass} ${isCancelTransaction ? 'font-semibold text-[#b91c1c]' : ''}`}>{formatWon(row.amount)}</td>
                          <td className={`px-3 py-1 text-right tabular-nums w-[95px]${rowStateClass}`}>{formatWon(row.fee)}</td>
                          <td className={`px-3 py-1 w-[90px] whitespace-nowrap truncate${rowStateClass}`}>{row.driverName || row.driverId || '-'}</td>
                          <td className={`px-3 py-1 text-center w-[72px] whitespace-nowrap${rowStateClass}`}>
                            {isCancelTransaction ? (
                              <span className="inline-flex rounded bg-[#fee2e2] px-1.5 py-0.5 font-semibold text-[#b91c1c]" title={row.status}>취소건</span>
                            ) : canceledApproval ? (
                              <span className="inline-flex rounded bg-[#dee2e6] px-1.5 py-0.5 font-semibold text-[#4e5968]" title="취소된 원결제">원결제 <small className="ml-1 text-[9px]">취소</small></span>
                            ) : (
                              <span className="inline-flex rounded bg-[#e8f3ff] px-1.5 py-0.5 font-semibold text-[#1d4ed8]" title={row.status}>원결제</span>
                            )}
                          </td>
                          <td className={`px-3 py-1 text-center w-[62px]${rowStateClass}`}>
                            <button
                              type="button"
                              onClick={() => openReceiptPreview(row)}
                              className="px-2 py-0.5 rounded text-[12px] font-semibold text-[var(--brand-primary)] border border-[var(--brand-primary)] bg-white hover:bg-[var(--brand-primary-soft)] shrink-0"
                            >
                              전표
                            </button>
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      </div>

      {pharmacyNameModalOpen && pharmacyNameTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={closePharmacyNameModal}>
          <div className="bg-white w-full max-w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h3 className="text-[24px] font-black text-[#191f28]">약국명 변경</h3>
              <button
                type="button"
                onClick={closePharmacyNameModal}
                disabled={pharmacyNameSaving}
                className="p-2 text-[#adb5bd] hover:text-[#191f28] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8 space-y-4">
              <div className="rounded-xl bg-[#f9fafb] border border-[#eef1f4] px-4 py-3 text-[13px]">
                <div className="flex items-start justify-between gap-3">
                  <span className="shrink-0 font-bold text-[#8b95a1]">현재 약국명</span>
                  <span className="min-w-0 text-right font-semibold text-[#191f28] break-words">{pharmacyNameTarget.pharmacy || '-'}</span>
                </div>
                <div className="mt-2 flex items-start justify-between gap-3">
                  <span className="shrink-0 font-bold text-[#8b95a1]">승인번호</span>
                  <span className="min-w-0 text-right font-mono text-[#4e5968] break-words">{pharmacyNameTarget.approvalNo || '-'}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-bold text-[#8b95a1]">변경할 약국명</label>
                <input
                  ref={pharmacyNameInputRef}
                  value={pharmacyNameDraft}
                  onChange={(e) => setPharmacyNameDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !pharmacyNameSaving) handlePharmacyNameSave(); }}
                  maxLength={100}
                  disabled={pharmacyNameSaving}
                  placeholder="약국명을 입력하세요"
                  className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-bold text-[#8b95a1]">변경 사유 <span className="font-medium text-[#adb5bd]">선택</span></label>
                <textarea
                  value={pharmacyNameReason}
                  onChange={(e) => setPharmacyNameReason(e.target.value)}
                  maxLength={500}
                  rows={4}
                  disabled={pharmacyNameSaving}
                  placeholder="예: 담당자 입력 오류"
                  className="w-full resize-none px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                />
                <div className="text-right text-[11px] text-[#9ca3af]">{pharmacyNameReason.length}/500</div>
              </div>
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button
                type="button"
                onClick={closePharmacyNameModal}
                disabled={pharmacyNameSaving}
                className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px] disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handlePharmacyNameSave}
                disabled={pharmacyNameSaving}
                className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20 inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:bg-[var(--brand-primary-disabled)]"
              >
                {pharmacyNameSaving ? '변경 중...' : <><Check size={18} strokeWidth={2.5} />변경</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {MERCHANT_TERMINAL_REGISTRATION_ENABLED && registerOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h3 className="text-[24px] font-medium text-[#191f28]">영업자 등록</h3>
              <button onClick={() => setRegisterOpen(false)} className="p-2 text-[#adb5bd] hover:text-[#191f28]">
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8">
              <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)] mb-6">
                <div className="p-2 bg-white rounded-lg">
                  <Plus size={16} className="text-[var(--brand-primary)]" />
                </div>
                <span className="text-[13px] font-bold text-[var(--brand-primary)]">
                  영업자 계정을 등록합니다.
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1] inline-flex items-center gap-1.5">
                    <User size={14} className="text-[#8b95a1]" />
                    영업자 ID (선택)
                  </label>
                  <input
                    value={registerForm.terminalId}
                    onChange={(e) => setRegisterForm((p) => ({ ...p, terminalId: stripHangul(e.target.value) }))}
                    placeholder="미입력 시 CPID_전화번호로 자동 생성"
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1] inline-flex items-center gap-1.5">
                    <Lock size={14} className="text-[#8b95a1]" />
                    초기 임시비밀번호
                  </label>
                  <input
                    type="password"
                    value={registerForm.tempPassword}
                    onChange={(e) => setRegisterForm((p) => ({ ...p, tempPassword: e.target.value }))}
                    placeholder="임시비밀번호를 입력하세요"
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1]">이름</label>
                  <input
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="이름을 입력하세요"
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1]">연락처</label>
                  <input
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
                    placeholder="연락처를 입력하세요"
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                  />
                </div>
              </div>
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button onClick={() => setRegisterOpen(false)} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]">
                취소
              </button>
              <button onClick={handleRegister} className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20">
                등록
              </button>
            </div>
          </div>
        </div>
      )}

      {editOpen && editTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h3 className="text-[24px] font-black text-[#191f28]">영업자 정보 수정</h3>
              <button onClick={() => setEditOpen(false)} className="p-2 text-[#adb5bd] hover:text-[#191f28]">
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8">
              <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)] mb-6">
                <div className="p-2 bg-white rounded-lg">
                  <Pencil size={16} className="text-[var(--brand-primary)]" />
                </div>
                <span className="text-[13px] font-bold text-[var(--brand-primary)]">
                  이름과 연락처를 수정할 수 있습니다.
                </span>
              </div>
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1]">영업자 아이디 (ID)</label>
                  <input
                    value={editTarget.driverId || '-'}
                    disabled
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] font-medium text-[#4e5968] cursor-not-allowed select-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-[#8b95a1]">이름</label>
                    <input
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="이름을 입력하세요"
                      className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-[13px] font-bold text-[#8b95a1]">연락처</label>
                    <input
                      value={editForm.phone}
                      onChange={(e) => setEditForm((p) => ({ ...p, phone: formatPhone(e.target.value) }))}
                      placeholder="연락처를 입력하세요"
                      className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button onClick={() => setEditOpen(false)} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]">
                취소
              </button>
              <button onClick={handleEditSave} className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20">
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {pwdOpen && pwdTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h3 className="text-[24px] font-black text-[#191f28]">비밀번호 변경</h3>
              <button
                type="button"
                onClick={() => { setPwdOpen(false); setPwdTarget(null); setPwdNew(''); }}
                className="p-2 text-[#adb5bd] hover:text-[#191f28]"
              >
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8">
              <div className="p-4 bg-[var(--brand-primary-soft)] rounded-2xl flex items-center gap-3 border border-[var(--brand-primary-border)] mb-6">
                <div className="p-2 bg-white rounded-lg">
                  <KeyRound size={16} className="text-[var(--brand-primary)]" />
                </div>
                <span className="text-[13px] font-bold text-[var(--brand-primary)]">
                  로그인 비밀번호를 변경합니다.
                </span>
              </div>
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1]">영업자 아이디 (ID)</label>
                  <input
                    value={pwdTarget.driverId || '-'}
                    disabled
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] font-medium text-[#4e5968] cursor-not-allowed select-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#8b95a1] inline-flex items-center gap-1.5">
                    <Lock size={14} className="text-[#8b95a1]" />
                    새 비밀번호
                  </label>
                  <input
                    type="password"
                    value={pwdNew}
                    onChange={(e) => setPwdNew(e.target.value)}
                    placeholder="새 비밀번호를 입력하세요"
                    className="w-full px-4 py-3 rounded-xl border border-transparent bg-[#f2f4f6] text-[15px] text-[#191f28] placeholder:text-[#9aa4b2] focus:border-[var(--brand-primary)] focus:outline-none"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button
                type="button"
                onClick={() => { setPwdOpen(false); setPwdTarget(null); setPwdNew(''); }}
                className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]"
              >
                취소
              </button>
              <button type="button" onClick={handlePasswordSave} className="flex-1 py-4 bg-[var(--brand-primary)] text-white rounded-2xl font-bold text-[16px] shadow-lg shadow-[var(--brand-primary)]/20">
                변경
              </button>
            </div>
          </div>
        </div>
      )}

      {statusModalOpen && statusModalTarget && statusModalNext && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[480px] rounded shadow-2xl overflow-hidden flex flex-col border border-[#e5e8eb]">
            <div className="px-8 py-6 flex items-center justify-between border-b border-[#f2f4f6]">
              <h3 className="text-[24px] font-black text-[#191f28]">영업자 상태 변경</h3>
              <button type="button" onClick={closeStatusModal} className="p-2 text-[#adb5bd] hover:text-[#191f28]">
                <X size={24} />
              </button>
            </div>
            <div className="py-5 px-8">
              <div
                className={`p-4 rounded-2xl flex items-center gap-3 border mb-6 ${
                  statusModalNext === 'INACTIVE'
                    ? 'bg-amber-50 border-amber-100'
                    : 'bg-emerald-50 border-emerald-100'
                }`}
              >
                <div className={`p-2 bg-white rounded-lg ${statusModalNext === 'INACTIVE' ? 'text-amber-600' : 'text-emerald-600'}`}>
                  <Power size={16} strokeWidth={2} />
                </div>
                <span className={`text-[13px] font-bold ${statusModalNext === 'INACTIVE' ? 'text-amber-800' : 'text-emerald-800'}`}>
                  {statusModalNext === 'INACTIVE'
                    ? '비활성화 시 해당 영업자는 로그인·업무 처리가 제한될 수 있습니다.'
                    : '활성화하면 해당 영업자 계정을 다시 사용할 수 있습니다.'}
                </span>
              </div>
              <p className="text-[15px] text-[#191f28] leading-relaxed">
                <span className="font-bold">{statusModalTarget.name || statusModalTarget.driverId || '-'}</span>
                {statusModalNext === 'INACTIVE' ? ' 을(를) 비활성화할까요?' : ' 을(를) 활성화할까요?'}
              </p>
            </div>
            <div className="px-8 py-6 bg-[#f9fafb] border-t border-[#f2f4f6] flex gap-3">
              <button type="button" onClick={closeStatusModal} className="flex-1 py-4 bg-[#e5e8eb] text-[#4e5968] rounded-2xl font-bold text-[16px]">
                취소
              </button>
              <button
                type="button"
                onClick={confirmStatusChange}
                className={`flex-1 py-4 rounded-2xl font-bold text-[16px] text-white shadow-lg ${
                  statusModalNext === 'INACTIVE'
                    ? 'bg-amber-600 shadow-amber-600/20 hover:bg-amber-700'
                    : 'bg-emerald-600 shadow-emerald-600/20 hover:bg-emerald-700'
                }`}
              >
                {statusModalNext === 'INACTIVE' ? '비활성화' : '활성화'}
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION === 2 && (
        <ReceiptPreviewModalV2
          data={receiptPreviewData}
          viewMode={receiptViewMode}
          onViewModeChange={setReceiptViewMode}
          onRevealCardNumber={() => setRevealModalOpen(true)}
          sendMethod={sendMethod}
          onSendMethodChange={setSendMethod}
          email={receiptEmail}
          onEmailChange={setReceiptEmail}
          phone={receiptPhone}
          onPhoneChange={setReceiptPhone}
          onSend={handleConfirmSendReceipt}
          onClose={() => {
            if (isSendingReceipt) return;
            setReceiptModalOpen(false);
            setReceiptEmail('');
            setReceiptPhone('');
            setSendMethod('email');
            setReceiptPreviewData(null);
            setReceiptViewMode('approval');
          }}
          isSending={isSendingReceipt}
          smsEnabled={RECEIPT_SMS_ENABLED}
          kakaoEnabled={RECEIPT_KAKAO_ENABLED}
        />
      )}
      {/* 영수증 미리보기 모달 (UI 1) */}
      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION !== 2 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto py-8" onClick={() => { if (isSendingReceipt) return; setReceiptModalOpen(false); setReceiptEmail(''); setReceiptPhone(''); setSendMethod('email'); setReceiptPreviewData(null); setReceiptViewMode('approval'); }}>
          <div className="bg-white rounded border border-[#e0e0e0] w-[360px] shadow-xl my-auto flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-4 border-b border-[#333333] flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-[18px] font-bold text-[#111111] leading-tight">{RECEIPT_LABELS.titleKo}</div>
                <div className="mt-0.5 text-[11px] font-semibold leading-tight text-[#767676]">{RECEIPT_LABELS.titleEn}</div>
              </div>
              {receiptPreviewData.isCanceled && (
                <div className="flex rounded overflow-hidden border border-[#e0e0e0] shrink-0">
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('approval')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${
                      receiptViewMode === 'approval'
                        ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                        : 'bg-white text-[var(--brand-primary)] border border-transparent hover:bg-[var(--brand-primary-soft)]'
                    }`}
                  >
                    승인
                  </button>
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('cancel')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${
                      receiptViewMode === 'cancel'
                        ? 'bg-[#b91c1c] text-white border-[#b91c1c]'
                        : 'bg-white text-[#b91c1c] border border-transparent hover:bg-[#fef2f2]'
                    }`}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-5 min-w-0">
              <div className="mb-0">
                <div className="text-[16px] font-bold text-[#111111] pb-3">거래정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드종류</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.cardBrand}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드번호</span>
                    <div className="flex items-center gap-2 ml-auto min-w-0">
                      <span className="text-[#111111] font-medium text-right min-w-0">
                        {receiptPreviewData.cardNumberMasked}
                      </span>
                      <button
                        type="button"
                        onClick={() => setRevealModalOpen(true)}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#d1d5db] text-[#4b5563] hover:bg-[#f3f4f6] shrink-0"
                      >
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">할부</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.installmentMonth}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래유형</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptViewMode === 'approval' ? '승인' : '취소'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">승인번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">
                      {receiptPreviewData.approvalNumber}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">
                      {receiptViewMode === 'approval' ? '거래일시' : '취소일시'}
                    </span>
                    <span className="text-[#111111] font-medium text-right min-w-0 leading-snug">
                      {receiptViewMode === 'approval'
                        ? receiptPreviewData.approvalDateTime
                        : receiptPreviewData.cancelDateTime ?? '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래처명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.pharmacyName}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">공급대가</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">과세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">
                      {receiptPreviewData.taxableAmount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">부가세</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">
                      {receiptPreviewData.vatAmount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">면세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">
                      {receiptPreviewData.taxFreeAmount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">봉사료</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">
                      {receiptPreviewData.serviceAmount}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#111111] font-bold text-[15px] shrink-0 w-[120px]">
                      총 결제금액
                    </span>
                    <span className="text-[#f04438] font-bold text-[16px] text-right min-w-0 tabular-nums">
                      {receiptPreviewData.totalAmount}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.storeInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.merchantName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.merchantOwnerName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">
                      {receiptPreviewData.merchantBusinessNo}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.merchantContact}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">
                      사업장소재지
                    </span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">
                      {receiptPreviewData.merchantAddress}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pspInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.supplierName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.supplierOwnerName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">
                      {receiptPreviewData.supplierBusinessNo}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.supplierContact}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">
                      사업장소재지
                    </span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">
                      {receiptPreviewData.supplierAddress}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div className="mb-5">
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pgInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">가맹점명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.cardMerchantName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.cardMerchantOwnerName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">
                      {receiptPreviewData.cardMerchantBusinessNo}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">
                      {receiptPreviewData.cardMerchantContact}
                    </span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">
                      사업장소재지
                    </span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">
                      {receiptPreviewData.cardMerchantAddress}
                    </span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="space-y-2 text-[11px] leading-relaxed text-[#999999]">
                <div>본 전표는 이메일 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.</div>
                <div className="text-[#666666]">{RECEIPT_CARD_SMS_NOTICE}</div>
              </div>
            </div>

            <div className="border-t border-[#eeeeee] p-5 bg-[#fafafa] shrink-0">
              {/* 전송 방법 선택 */}
              <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] mb-3">
                <button type="button" onClick={() => setSendMethod('email')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'email' ? 'bg-[var(--brand-primary)] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>이메일</button>
                <button type="button" onClick={() => { if (RECEIPT_SMS_ENABLED) setSendMethod('sms'); }} className={`flex-1 py-2 text-[13px] font-semibold border-x border-[#e5e7eb] ${!RECEIPT_SMS_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_SMS_ENABLED ? '문자' : '문자(준비중)'}</button>
                <button type="button" onClick={() => { if (RECEIPT_KAKAO_ENABLED) setSendMethod('kakao'); }} className={`flex-1 py-2 text-[13px] font-semibold ${!RECEIPT_KAKAO_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e]' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_KAKAO_ENABLED ? '카카오톡' : '카카오톡(준비중)'}</button>
              </div>
              {sendMethod === 'email' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">이메일 주소</label>
                  <input
                    type="email"
                    value={receiptEmail}
                    onChange={(e) => setReceiptEmail(e.target.value)}
                    autoComplete="off"
                    placeholder="example@email.com"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                </>
              )}
              {sendMethod === 'sms' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input
                    type="tel"
                    value={receiptPhone}
                    onChange={(e) => setReceiptPhone(e.target.value)}
                    placeholder="01012345678"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                </>
              )}
              {sendMethod === 'kakao' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }} className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]" />
                </>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isSendingReceipt) return;
                    setReceiptModalOpen(false);
                    setReceiptEmail('');
                    setReceiptPhone('');
                    setSendMethod('email');
                    setReceiptPreviewData(null);
                    setReceiptViewMode('approval');
                  }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold bg-[#e5e7eb] text-[#111827] hover:bg-[#d1d5db]"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSendReceipt}
                  disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold ${(sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e] hover:bg-[#f0d900]' : isSendingReceipt ? 'bg-[var(--brand-primary-disabled)] text-white cursor-not-allowed' : sendMethod === 'sms' ? 'bg-[#00b493] text-white hover:bg-[#009a7f]' : 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]'}`}
                >
                  {isSendingReceipt ? '전송중...' : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? '준비중' : sendMethod === 'kakao' ? '알림톡 전송' : sendMethod === 'sms' ? '문자 전송' : '이메일 전송'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 카드번호 조회 모달 */}
      {revealModalOpen && receiptPreviewData && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#111827]">카드번호 조회</h2>
              <button
                type="button"
                onClick={() => {
                  if (revealLoading) return;
                  setRevealModalOpen(false);
                  setRevealPassword('');
                  setRevealReason('');
                }}
                className="text-[#9ca3af] hover:text-[#111827] text-[18px] leading-none px-1"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-[13px] text-[#374151]">
              <p className="text-[13px] text-[#4b5563]">
                카드번호는 개인정보 보호를 위해 기본적으로 완전 마스킹 처리됩니다.
                카드번호의 앞 6자리/뒤 4자리를 확인하려면, 아래 정보를 입력해 주세요.
              </p>

              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">
                  비밀번호
                </label>
                <input
                  type="password"
                  value={revealPassword}
                  onChange={(e) => setRevealPassword(e.target.value)}
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="로그인 비밀번호를 입력하세요"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">
                  조회 사유
                </label>
                <input
                  type="text"
                  value={revealReason}
                  onChange={(e) => setRevealReason(e.target.value)}
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="예: 카드사 문의, 고객 요청 등"
                />
              </div>

              <p className="text-[11px] text-[#9ca3af]">
                입력하신 정보는 카드번호 조회 내역과 함께 감사 로그로 기록될 수 있습니다.
              </p>
            </div>

            <div className="px-5 py-3 border-t border-[#e5e7eb] flex justify-end gap-2 bg-[#f9fafb]">
              <button
                type="button"
                onClick={() => {
                  if (revealLoading) return;
                  setRevealModalOpen(false);
                  setRevealPassword('');
                  setRevealReason('');
                }}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-white text-[#4b5563] border border-[#d1d5db]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleRevealCardNumber}
                disabled={revealLoading}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-[#111827] text-white disabled:opacity-60"
              >
                {revealLoading ? '조회 중...' : '조회'}
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="shrink-0 py-[0.5rem] px-[1.5rem] flex items-center justify-between text-[12px] text-[#8b95a1] border-t border-[#f2f4f6]">
        <button
          type="button"
          onClick={toggleShowAll}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${showAllOpen ? 'bg-[var(--brand-primary)] text-white' : 'bg-[#e5e8eb] text-[#8b95a1] hover:bg-[#d0d7de]'}`}
          title="전체 툴팁 보기 (n)"
        >
          <HelpCircle size={14} strokeWidth={2} />
          <span>툴팁안내 (n)</span>
        </button>
        <span>{BRAND_COPYRIGHT}</span>
      </footer>
    </div>
  );
}

export default DeliveryManage;
